import {useEffect, useMemo, useRef, useState} from 'react'
import {Check, ChevronDown, Search, X} from 'lucide-react'

/**
 * Compact searchable select for the ~20-entry profession list. Replaces
 * the ChipSelector wall of role chips in the listing forms: one field-like
 * row when collapsed, an anchored search-and-pick popover when open.
 *
 * Options follow the constants-file shape ({value, name}); `value` is an
 * array of value strings, so it reads/writes the exact state keys the old
 * ChipSelector wrote. Multi-select keeps the popover open on toggle;
 * single-select closes on pick.
 */
const ProfessionPicker = ({
  options = [],
  value = [],
  onChange,
  multiple = true,
  placeholder = 'Select professions…',
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef(null)
  const searchRef = useRef(null)

  // Close on outside click while open.
  useEffect(() => {
    if (!open) return undefined
    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  // Fresh search + autofocus each time the popover opens.
  useEffect(() => {
    if (!open) return undefined
    setQuery('')
    const t = setTimeout(() => searchRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.name.toLowerCase().includes(q))
  }, [options, query])

  const selectedOptions = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value]
  )

  const toggle = (val) => {
    if (multiple) {
      onChange(
        value.includes(val) ? value.filter((v) => v !== val) : [...value, val]
      )
    } else {
      onChange(value.includes(val) ? [] : [val])
      setOpen(false)
    }
  }

  // Escape closes just the popover — stopPropagation keeps the composer
  // shell's Escape-to-close from also firing.
  const handleRootKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation()
      setOpen(false)
    }
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered.length > 0) toggle(filtered[0].value)
    }
  }

  return (
    <div ref={rootRef} className="relative" onKeyDown={handleRootKeyDown}>
      <div
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left cursor-pointer flex items-center justify-between gap-2 focus:outline-none focus:border-sharpenn-coral focus:ring-2 focus:ring-sharpenn-coral/20 transition-colors"
      >
        {selectedOptions.length > 0 ? (
          <span className="flex flex-wrap gap-1 min-w-0">
            {selectedOptions.map((o) => (
              <span
                key={o.value}
                className="inline-flex items-center gap-1 rounded-full bg-sharpenn-blush text-sharpenn-charcoal text-xs px-2 py-0.5"
              >
                {o.name}
                <button
                  type="button"
                  aria-label={`Remove ${o.name}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggle(o.value)
                  }}
                  className="hover:text-sharpenn-coral transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </span>
        ) : (
          <span className="text-sm text-sharpenn-slate/70">{placeholder}</span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-sharpenn-slate flex-shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-2xl border border-gray-100 bg-white shadow-large overflow-hidden">
          <div className="flex items-center gap-2 px-3 border-b border-gray-100">
            <Search className="w-4 h-4 text-sharpenn-slate/60 flex-shrink-0" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search…"
              className="w-full border-0 bg-transparent py-2.5 text-sm text-sharpenn-black placeholder:text-green-gray focus:outline-none focus:ring-0"
            />
          </div>
          <ul
            role="listbox"
            aria-multiselectable={multiple}
            className="max-h-56 overflow-y-auto py-1"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-sharpenn-slate/70">
                No matches
              </li>
            ) : (
              filtered.map((o) => {
                const isSelected = value.includes(o.value)
                return (
                  <li
                    key={o.value}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => toggle(o.value)}
                    className={`px-3 py-2 text-sm flex items-center justify-between hover:bg-sharpenn-cream cursor-pointer ${
                      isSelected
                        ? 'font-medium text-sharpenn-charcoal'
                        : 'text-sharpenn-slate'
                    }`}
                  >
                    {o.name}
                    {isSelected && (
                      <Check className="w-4 h-4 text-sharpenn-coral flex-shrink-0" />
                    )}
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

export default ProfessionPicker
